import {
  MarketButton, MarketButtonDropdown, MarketAccessory, MarketDivider, MarketList, MarketRow, MarketInlineStatus,
} from '@market/react';
import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCashAppLocalTracking,
  Feature,
  Activity,
  EventName,
} from '@squareup/service-cash-local-hooks';
import { useErrorTracking } from '@squareup/service-error-tracking';
import type { BrandLocationConfiguration } from '@squareup/shared-types-protos/squareup/cash/local/v1/seller/cash_app_local_seller_service.js';
import { EnablementStatus } from '@squareup/shared-types-protos/squareup/channelcontrols/configuration/enums.js';
import GlobeSvg from '../../assets/svg/globe.svg';
import { useCashAppDefaultBrand, useLocationOrderingConfigByIdMap, useUpdateOrderingConfiguration } from '../../hooks/useCashApp.js';
import {
  useLocationIdToFulfillmentConfigurationMap,
  useFulfillmentEnablementUpdater,
  FulfillmentType,
  type LocationFulfillmentEnablementUpdates,
} from '../../hooks/useFulfillmentConfiguration.js';
import { useActiveLocations } from '../../hooks/useLocations.js';
import {
  useFulfillmentEnablementSaveSuccessToast,
  useFulfillmentEnablementSaveErrorToast,
  useOrderingConfigurationSuccessToast,
  useOrderingConfigurationErrorToast,
  usePrepTimeSaveSuccessToast,
  usePrepTimeSaveErrorToast,
} from '../../hooks/useToasts.js';
import { parsePrepTimeString } from '../../util/prep-time-utils.js';
import { FulfillmentEnablementLocationModal } from '../fulfillment-enablement-location-modal/fulfillment-enablement-location-modal.js';
import { OrderingStatusLocationModal } from '../ordering-status-location-modal/ordering-status-location-modal.js';
import { PrepTimeModal } from '../prep-time-modal/prep-time-modal.js';
import { ProfileInfo } from '../profile-info/profile-info.js';
import styles from './button-dropdown.module.css';

export interface ButtonDropdownProps {
  size?: 'small' | 'medium' | 'large';
  showOrderingStatus?: boolean;
  eventLocation?: string;
  hideEditing?: boolean;
  onOrderingConfigurationUpdate?: (orderingPaused: boolean) => void;
  onPreviewSite?: () => void;
  onCompleteProfile: () => void;
  onEditProfileClick: () => void;
}

export function ButtonDropdown({
  size = 'medium',
  showOrderingStatus = false,
  hideEditing = false,
  onOrderingConfigurationUpdate,
  onPreviewSite,
  onCompleteProfile,
  onEditProfileClick,
  eventLocation = '',
}: ButtonDropdownProps) {
  const { t } = useTranslation();
  const { captureException } = useErrorTracking();
  const { trackEvent } = useCashAppLocalTracking();

  // Toasts
  const showFulfillmentEnablementSaveSuccessToast = useFulfillmentEnablementSaveSuccessToast();
  const showFulfillmentEnablementSaveErrorToast = useFulfillmentEnablementSaveErrorToast();
  const showOrderingConfigurationSuccessToast = useOrderingConfigurationSuccessToast();
  const showOrderingConfigurationErrorToast = useOrderingConfigurationErrorToast();
  const showPrepTimeSaveSuccessToast = usePrepTimeSaveSuccessToast();
  const showPrepTimeSaveErrorToast = usePrepTimeSaveErrorToast();

  // Data
  const { data: defaultBrandData } = useCashAppDefaultBrand();
  const shouldHideEditing = hideEditing || defaultBrandData?.hasUnifiedChannels === true;
  const { data: locationsData } = useActiveLocations();
  const locations = useMemo(() => locationsData?.locations || [], [locationsData]);
  const locationOrderingConfigByIdMap = useLocationOrderingConfigByIdMap();
  const locationIdToFulfillmentConfigurationMap = useLocationIdToFulfillmentConfigurationMap(locations.reduce((acc, location) => {
    if (location.id) {
      acc.push(location.id);
    }
    return acc;
  }, [] as string[]));

  // Mutations
  const { mutateAsync: updateOrderingConfiguration } = useUpdateOrderingConfiguration();
  const {
    updateLocationFulfillmentEnabled,
    updateLocationsFulfillmentEnabled,
  } = useFulfillmentEnablementUpdater();

  // Local state
  const [isOrderingStatusModalOpen, setIsOrderingStatusModalOpen] = useState(false);
  const [isPrepTimeModalOpen, setIsPrepTimeModalOpen] = useState(false);
  const [isFulfillmentModalOpen, setIsFulfillmentModalOpen] = useState(false);
  const [fulfillmentModalType, setFulfillmentModalType] = useState<FulfillmentType>(FulfillmentType.Pickup);

  // Computed values
  // Generic function to compute fulfillment enablement display for pickup or delivery
  const getFulfillmentEnablementDisplay = useCallback(
    (fulfillmentType: FulfillmentType): string => {
      const settingsKey = fulfillmentType === FulfillmentType.Pickup ? 'pickupSettings' : 'deliverySettings';
      const enablements = Object.values(locationIdToFulfillmentConfigurationMap).map(
        (configuration) => configuration?.[settingsKey]?.enablementStatus || EnablementStatus.DISABLED,
      );

      if (enablements.length === 0) {
        return t('buttonDropdown.pickupOff');
      }

      // If all locations have same enablement status, then show that value
      if (enablements.every((val) => val === enablements[0])) {
        return enablements[0] === EnablementStatus.ENABLED
          ? t('buttonDropdown.pickupOn')
          : t('buttonDropdown.pickupOff');
      }

      // Otherwise show # of enabled locations
      const enabledCount = enablements.filter((status) => status === EnablementStatus.ENABLED).length;
      return t('buttonDropdown.locationsCount', { count: enabledCount });
    },
    [locationIdToFulfillmentConfigurationMap, t],
  );

  const pickupEnablementDisplay = useMemo(
    () => getFulfillmentEnablementDisplay(FulfillmentType.Pickup),
    [getFulfillmentEnablementDisplay],
  );

  const deliveryEnablementDisplay = useMemo(
    () => getFulfillmentEnablementDisplay(FulfillmentType.Delivery),
    [getFulfillmentEnablementDisplay],
  );

  const showCompleteProfileSetup = useMemo(() => {
    if (defaultBrandData?.orderingPaused) {
      return true;
    }

    // No locations are configured
    const locationConfigurations = defaultBrandData?.locationConfigurations;
    if (locationConfigurations?.length === 0) {
      return true;
    }

    return false;
  }, [defaultBrandData]);

  const prepTimeDisplay = useMemo(() => {
    const prepTimes: string[] = [];

    // Collect both pickup and delivery prep times from all configurations
    Object.values(locationIdToFulfillmentConfigurationMap).forEach((configuration) => {
      const pickupTime = configuration?.pickupSettings?.prepTimeDuration;
      const deliveryTime = configuration?.deliverySettings?.prepTimeDuration;
      if (pickupTime) {
        prepTimes.push(pickupTime);
      }
      if (deliveryTime) {
        prepTimes.push(deliveryTime);
      }
    });

    // If no prep times are set, return default
    if (prepTimes.length === 0) {
      return t('prepTime.mins', { count: 15 });
    }

    // If all prep times are the same, show that value
    const allEqual = prepTimes.every((time: string) => time === prepTimes[0]);
    if (allEqual) {
      try {
        const quantity = parsePrepTimeString(prepTimes[0]);
        return t('prepTime.mins', { count: quantity });
      } catch {
        return t('prepTime.noPrep');
      }
    }

    return t('prepTime.prepTimesVary');
  }, [locationIdToFulfillmentConfigurationMap, t]);

  const hasLocationConfigurations = !!defaultBrandData?.locationConfigurations?.length;
  const disableDropdownControlsListProp = showCompleteProfileSetup;
  const isAllLocationOrderingPaused = useMemo(
    () => defaultBrandData?.locationConfigurations?.every((config) => config.orderConfiguration?.orderingPaused) || !hasLocationConfigurations,
    [defaultBrandData?.locationConfigurations, hasLocationConfigurations],
  );

  // Action handlers
  const handlePrepTimeModalFlow = useCallback(() => {
    trackEvent(
      Feature.LOCAL_PROFILES,
      EventName.VIEW_LOCAL_PROFILE_PREP_TIME_MODAL,
      Activity.MANAGE_PROFILE_FULFILLMENT,
      {
        fulfillment_type: 'prep_time',
        event_location: eventLocation,
      },
    );
    setIsPrepTimeModalOpen(true);
  }, [eventLocation, trackEvent]);

  const updateOrdering = useCallback(async (locations: BrandLocationConfiguration[]) => {
    try {
      await updateOrderingConfiguration({
        locationConfigurations: {
          locationConfiguration: locations,
        },
      });

      // Update on parent component
      const isOrderingPaused = locations?.every((config) => config.orderConfiguration?.orderingPaused) || locations.length === 0;
      onOrderingConfigurationUpdate?.(isOrderingPaused);

      // Show success toast
      showOrderingConfigurationSuccessToast(isOrderingPaused, locations.length);
    } catch (error) {
      // Show error toast
      showOrderingConfigurationErrorToast();

      captureException(new Error('Error updating ordering configuration'), {
        captureContext: {
          extra: {
            error,
          },
        },
      });
      throw error;
    }
  }, [captureException, onOrderingConfigurationUpdate, updateOrderingConfiguration, showOrderingConfigurationSuccessToast, showOrderingConfigurationErrorToast]);

  const cashLocalDropdownOpened = useCallback(() => {
    trackEvent(
      Feature.LOCAL_PROFILES,
      EventName.VIEW_LOCAL_PROFILE_MANAGEMENT,
      Activity.MANAGE_PROFILE,
      {
        event_location: eventLocation,
      },
    );
  }, [trackEvent, eventLocation]);

  const handleOrderingModalFlow = useCallback(() => {
    if (locations.length === 0) {
      return;
    }

    if (locations.length === 1) {
      // For single location, skip modal and just toggle ordering state.
      const orderingConfig = locationOrderingConfigByIdMap[locations[0].id || ''];
      if (orderingConfig) {
        updateOrdering([
          {
            ...orderingConfig,
            orderConfiguration: {
              orderingPaused: !orderingConfig.orderConfiguration?.orderingPaused,
            },
          },
        ]);
      }
    } else {
      // Open ordering status location modal for multi-location
      setIsOrderingStatusModalOpen(true);
    }
  }, [locations, locationOrderingConfigByIdMap, updateOrdering]);

  const handleFulfillmentToggle = useCallback(async (fulfillmentType: FulfillmentType) => {
    if (locations.length === 0) {
      return;
    }

    const settingsKey = fulfillmentType === FulfillmentType.Pickup ? 'pickupSettings' : 'deliverySettings';

    if (locations.length === 1) {
      // For single location, skip modal and directly toggle fulfillment
      const locationId = locations[0].id;
      if (!locationId) {
        captureException(new Error('Location ID missing when updating fulfillment enablement for single location'));
        return;
      }
      const currentEnablement = locationIdToFulfillmentConfigurationMap[locationId]?.[settingsKey]?.enablementStatus || EnablementStatus.DISABLED;
      const updatedEnablement = !(currentEnablement === EnablementStatus.ENABLED);
      try {
        await updateLocationFulfillmentEnabled(fulfillmentType, updatedEnablement, locationIdToFulfillmentConfigurationMap[locationId]);
        // Show success toast for single location
        showFulfillmentEnablementSaveSuccessToast(fulfillmentType, { [locationId]: updatedEnablement });
      } catch (error) {
        // Show error toast
        showFulfillmentEnablementSaveErrorToast(fulfillmentType);
        captureException(new Error(`Error updating ${fulfillmentType === FulfillmentType.Pickup ? 'pickup' : 'delivery'} enablement for single location`), {
          captureContext: {
            extra: {
              error,
            },
          },
        });
      }
    } else {
      // Open fulfillment enablement modal for multi-location
      setFulfillmentModalType(fulfillmentType);
      setIsFulfillmentModalOpen(true);
    }
  }, [locations, locationIdToFulfillmentConfigurationMap, captureException, updateLocationFulfillmentEnabled, showFulfillmentEnablementSaveSuccessToast, showFulfillmentEnablementSaveErrorToast]);

  const handleFulfillmentModalSubmit = useCallback(async (selectedLocationIds: string[]) => {
    const settingsKey = fulfillmentModalType === FulfillmentType.Pickup ? 'pickupSettings' : 'deliverySettings';

    const locationsToUpdate = locations.reduce((result, location) => {
      if (!location.id) {
        captureException(new Error('Location ID missing when updating fulfillment enablement for multiple locations'));
        return result;
      }
      const intendedEnabled = selectedLocationIds.includes(location.id);
      const intendedEnablement = intendedEnabled ? EnablementStatus.ENABLED : EnablementStatus.DISABLED;
      // Skip if nothing changed
      if (intendedEnablement === locationIdToFulfillmentConfigurationMap[location.id]?.[settingsKey]?.enablementStatus) {
        return result;
      }
      return {
        ...result,
        [location.id]: intendedEnabled,
      };
    }, {} as LocationFulfillmentEnablementUpdates);

    try {
      await updateLocationsFulfillmentEnabled(fulfillmentModalType, locationsToUpdate, locationIdToFulfillmentConfigurationMap);
      showFulfillmentEnablementSaveSuccessToast(fulfillmentModalType, locationsToUpdate);
      setIsFulfillmentModalOpen(false);
    } catch (error) {
      // Show error toast
      showFulfillmentEnablementSaveErrorToast(fulfillmentModalType);
      captureException(new Error(`Error updating ${fulfillmentModalType === FulfillmentType.Pickup ? 'pickup' : 'delivery'} enablement for multiple locations`), {
        captureContext: {
          extra: {
            error,
          },
        },
      });
    }
  }, [fulfillmentModalType, locations, locationIdToFulfillmentConfigurationMap, captureException, updateLocationsFulfillmentEnabled, showFulfillmentEnablementSaveSuccessToast, showFulfillmentEnablementSaveErrorToast]);

  // Render trigger button
  const renderTriggerButton = () => {
    if (showOrderingStatus) {
      const isOrderingOff = defaultBrandData?.orderingPaused || isAllLocationOrderingPaused;
      return (
        <MarketButton rank="secondary" size={size} slot="trigger">
          <MarketAccessory slot="icon">
            <div className={`${styles.statusIndicator} ${isOrderingOff ? styles.statusIndicatorOff : styles.statusIndicatorOn}`} />
          </MarketAccessory>
          {isOrderingOff ? t('buttonDropdown.orderingOff') : t('buttonDropdown.orderingOn')}
        </MarketButton>
      );
    }

    return (
      <MarketButton rank="secondary" size={size} slot="trigger">
        <MarketAccessory slot="icon">
          <img
            src={GlobeSvg}
            alt="globe-icon"
          />
        </MarketAccessory>
        {t('buttonDropdown.orderingProfile')}
      </MarketButton>
    );
  };

  return (
    <>
      <MarketButtonDropdown
        interaction="click"
        slot="trailing-accessory"
        className={styles.localProfileDropdown}
        data-testid="cash-local-button-dropdown"
        onMarketButtonDropdownOpened={cashLocalDropdownOpened}
      >
        {renderTriggerButton()}

        <div slot="content" className={styles.cashLocalDropdownContent}>
          <ProfileInfo
            defaultBrandData={defaultBrandData}
            onEditProfileClick={onEditProfileClick}
          />

          {(showCompleteProfileSetup || !shouldHideEditing) && (
            <MarketDivider margin="small" size="thin" />
          )}

          {showCompleteProfileSetup && (
            <div className={styles.completeProfileContainer}>
              <div className={styles.completeProfileStatusMessage}>
                <MarketInlineStatus variant="info" data-testid="complete-profile-status">
                  {t('buttonDropdown.finishYourProfile')}
                </MarketInlineStatus>
              </div>
              <div className={styles.completeProfileButton}>
                <MarketButton
                  rank="primary"
                  size="small"
                  data-testid="complete-profile-button"
                  onClick={onCompleteProfile}
                >
                  {t('buttonDropdown.completeProfile')}
                </MarketButton>
              </div>
            </div>
          )}

          {!shouldHideEditing && (
            <MarketList className={styles.profileRowsContainer}>
              {/* Online Ordering Row */}
              <MarketRow
                size="small"
                data-testid="cash-local-ordering-row"
                className={disableDropdownControlsListProp ? styles.profileRowDisabled : ''}
                variant="drill"
                onClick={handleOrderingModalFlow}
              >
                <label slot="label" className={styles.profileRowTitle}>{t('buttonDropdown.onlineOrdering')}</label>
                <p slot="subtext" className={styles.profileRowSubtext}>
                  {isAllLocationOrderingPaused ? t('buttonDropdown.off') : t('buttonDropdown.on')}
                </p>
              </MarketRow>

              {/* Pickup Row */}
              <MarketRow
                size="small"
                data-testid="cash-local-pickup-row"
                className={disableDropdownControlsListProp ? styles.profileRowDisabled : ''}
                variant="drill"
                onClick={() => handleFulfillmentToggle(FulfillmentType.Pickup)}
              >
                <label slot="label" className={styles.profileRowTitle}>{t('buttonDropdown.pickup')}</label>
                <p slot="subtext" className={styles.profileRowSubtext} data-testid="cash-local-pickup-status">
                  {pickupEnablementDisplay}
                </p>
              </MarketRow>

              {/* Delivery Row */}
              <MarketRow
                size="small"
                data-testid="cash-local-delivery-row"
                className={disableDropdownControlsListProp ? styles.profileRowDisabled : ''}
                variant="drill"
                onClick={() => handleFulfillmentToggle(FulfillmentType.Delivery)}
              >
                <label slot="label" className={styles.profileRowTitle}>{t('buttonDropdown.delivery')}</label>
                <p slot="subtext" className={styles.profileRowSubtext} data-testid="cash-local-delivery-status">
                  {deliveryEnablementDisplay}
                </p>
              </MarketRow>

              {/* Prep Time Row */}
              <MarketRow
                size="small"
                data-testid="cash-local-prep-time-row"
                className={disableDropdownControlsListProp ? styles.profileRowDisabled : ''}
                variant="drill"
                onClick={handlePrepTimeModalFlow}
              >
                <label slot="label" className={styles.profileRowTitle}>{t('buttonDropdown.prepTime')}</label>
                <p slot="subtext" className={styles.profileRowSubtext} data-testid="cash-local-prep-time">
                  {prepTimeDisplay}
                </p>
              </MarketRow>
            </MarketList>
          )}

          {onPreviewSite && (
            <>
              <hr className={styles.sectionBreak} />
              <MarketRow>
                <MarketButton
                  className={styles.openPreviewButton}
                  rank="primary"
                  size="small"
                  onClick={onPreviewSite}
                >
                  {t('buttonDropdown.previewSite')}
                </MarketButton>
              </MarketRow>
            </>
          )}
        </div>
      </MarketButtonDropdown>

      {/* Modals */}
      {isOrderingStatusModalOpen && (
        <OrderingStatusLocationModal
          locationConfigurations={defaultBrandData?.locationConfigurations || []}
          fulfillmentConfigurations={locationIdToFulfillmentConfigurationMap}
          onSubmit={async (updatedConfigurations) => {
            await updateOrdering(updatedConfigurations);
            setIsOrderingStatusModalOpen(false);
          }}
          onDismiss={() => setIsOrderingStatusModalOpen(false)}
          onToggleTracking={(locationId: string, isEnabling: boolean) => {
            trackEvent(
              Feature.LOCAL_PROFILES,
              isEnabling ? EventName.CLICK_LOCAL_PROFILE_ENABLE_ORDERING : EventName.CLICK_LOCAL_PROFILE_DISABLE_ORDERING,
              Activity.MANAGE_PROFILE_FULFILLMENT,
              {
                unit_token: locationId,
                event_location: eventLocation,
              },
            );
          }}
        />
      )}

      {isFulfillmentModalOpen && (
        <FulfillmentEnablementLocationModal
          fulfillmentType={fulfillmentModalType}
          locationIdToFulfillmentConfigurationMap={locationIdToFulfillmentConfigurationMap}
          onSubmit={handleFulfillmentModalSubmit}
          onDismiss={() => setIsFulfillmentModalOpen(false)}
        />
      )}

      {isPrepTimeModalOpen && (
        <PrepTimeModal
          onClose={() => setIsPrepTimeModalOpen(false)}
          onSave={(success) => {
            if (success) {
              showPrepTimeSaveSuccessToast();
              trackEvent(
                Feature.LOCAL_PROFILES,
                EventName.CLICK_LOCAL_PROFILE_PREP_TIME_MODAL_SAVE,
                Activity.MANAGE_PROFILE_FULFILLMENT,
                {
                  fulfillment_type: 'prep_time',
                  event_location: eventLocation,
                },
              );
            } else {
              showPrepTimeSaveErrorToast();
            }
          }}
        />
      )}
    </>
  );
}

export default ButtonDropdown;
